import type { QUERY_ORDER } from 'elements/controller/query/query'
import { ComponentLikeType } from 'elements/hardwareSelectorV2/types'
import { useNotify } from 'ra-core'
import { useEffect, useMemo, useRef } from 'react'
import type { Identifier, Record as RaRecord, RecordMap } from 'react-admin'
import useHardwareComponentsListBasic from './useHardwareComponentsListBasic'

export const COMPONENTS_V2_RESOURCE = 'componentsv2'

/** Stable comma-separated `codes` query value for componentsv2 list filters (matches hardware filter nodes). */
export const joinCodesForComponentsV2Filter = (codes: string[]): string =>
  [...codes].filter(Boolean).sort().join(',')

export type UseComponentsV2ListArgs = {
  filterValues: Record<string, unknown>
  page?: number
  perPage?: number
  sortField?: string
  sortOrder?: QUERY_ORDER
  blockRequest?: boolean
  notifyOnError?: boolean
}

/**
 * Store-backed componentsv2 list reads via {@link useHardwareComponentsListBasic} (GET_LIST cache).
 * Replaces ad-hoc CUSTOM_GET to `orgs/{orgId}/componentsv2/` (see removed useFetchComponentsData).
 */
const useComponentsV2List = ({
  filterValues,
  page = 1,
  perPage = 10,
  sortField = '_default_rule',
  sortOrder = 'ASC',
  blockRequest = false,
  notifyOnError = true,
}: UseComponentsV2ListArgs): {
  componentsData: ComponentLikeType[]
  data?: RecordMap<RaRecord>
  ids?: Identifier[]
  total?: number
  loading: boolean
  loaded: boolean
  error?: unknown
} => {
  const notify = useNotify()
  const lastErrorNotifySigRef = useRef<string | null>(null)
  const { data, ids, total, loading, loaded: listQueryLoaded, error } = useHardwareComponentsListBasic({
    resource: COMPONENTS_V2_RESOURCE,
    page,
    perPage,
    sort: sortField,
    order: sortOrder,
    filterValues,
    blockRequest,
  })

  useEffect(() => {
    if (!notifyOnError) return
    if (listQueryLoaded && !error) {
      lastErrorNotifySigRef.current = null
      return
    }
    if (!listQueryLoaded || !error) return

    const requestKey = JSON.stringify({
      page,
      perPage,
      sortField,
      sortOrder,
      filterValues,
      blockRequest,
    })
    const errMsg = error instanceof Error ? error.message : String(error)
    const sig = `${requestKey}|${errMsg}`
    if (lastErrorNotifySigRef.current === sig) return
    lastErrorNotifySigRef.current = sig
    notify('There was an error when fetching component data.')
  }, [
    listQueryLoaded,
    error,
    notify,
    notifyOnError,
    page,
    perPage,
    sortField,
    sortOrder,
    filterValues,
    blockRequest,
  ])

  const componentsData = useMemo(() => {
    if (!ids?.length || !data) return []
    const out: ComponentLikeType[] = []
    for (const id of ids) {
      const row = data[id]
      if (row) out.push(row as ComponentLikeType)
    }
    return out
  }, [data, ids])

  // GET_LIST can leave ids in Redux cachedRequests while record rows are TTL-evicted from the pool;
  // react-admin then reports listQueryLoaded with an empty `data` map. Treat as not loaded for UI.
  const listRecordsHydrated = !ids?.length || componentsData.length === ids.length
  const loaded = listQueryLoaded && listRecordsHydrated

  return { componentsData, data, ids, total, loading, loaded, error }
}

export default useComponentsV2List
